// Редът „Общо" в дъното на решетката на работен лист — като последния ред на
// таблица: сумите стоят ТОЧНО под колоните си (същият grid template като
// редовете), а редът е лепнат за долния край на картата (sticky bottom-0), така
// че се вижда при всякакъв брой редове. При малко редове празното място остава
// НАД него: пълнеж със същите колонни линии, та таблицата да стига до сбора.
//
// Родителят на решетката трябва да е `flex min-h-full flex-col`, а
// скролиращата обвивка — с определена височина (картата „Продукти").
import type { ReactNode } from "react";
import { SHEET_TOTAL_ROW } from "../list/density";

// Статичните класове на Tailwind (col-span-N не може да се сглоби динамично)
const SPAN = [
  "",
  "col-span-1",
  "col-span-2",
  "col-span-3",
  "col-span-4",
  "col-span-5",
  "col-span-6",
  "col-span-7",
  "col-span-8",
  "col-span-9",
  "col-span-10",
  "col-span-11",
  "col-span-12",
] as const;

type Props = {
  // grid template-ът на редовете (LINE_GRID_CLASS на листа)
  gridClass: string;
  // колко колони има решетката — за пълнежа с колонните линии
  columnCount: number;
  children: ReactNode;
};

export const WorksheetTotalsRow = ({ gridClass, columnCount, children }: Props) => (
  <>
    <div aria-hidden className={`${gridClass} min-h-0 flex-1`}>
      {Array.from({ length: columnCount }, (_, index) => (
        <div
          key={index}
          className={`${index === 0 ? "bg-fill" : ""} ${index < columnCount - 1 ? "border-r border-shade-1" : ""}`}
        />
      ))}
    </div>
    <div
      role="row"
      className={`${gridClass} ${SHEET_TOTAL_ROW} sticky bottom-0 z-[4] border-t-2 border-shade-3 bg-fill text-[12.5px] leading-[normal]`}
    >
      {children}
    </div>
  </>
);

// „ОБЩО · 2 продукта" — заема първите `span` колони
export const TotalsLabelCell = ({
  span,
  title,
  detail,
}: {
  span: number;
  title: string;
  detail?: ReactNode;
}) => (
  <div className={`${SPAN[span]} flex min-w-0 items-center gap-2.5 border-r border-shade-2 px-[11px] py-1`}>
    <span className="text-[10.5px] font-bold uppercase tracking-[0.12em] text-muted">{title}</span>
    {detail && <span className="truncate text-[12px] text-body">{detail}</span>}
  </div>
);

// Сума под колоната си; `strong` — крайната (бруто), в кехлибарения тотален цвят
export const TotalsValueCell = ({
  value,
  strong,
  caption,
}: {
  value: ReactNode;
  strong?: boolean;
  // малък надпис над числото, когато колоната няма свой заглавен смисъл (ДДС сума)
  caption?: string;
}) => (
  <div className="flex min-w-0 flex-col items-end justify-center border-r border-shade-2 px-2.5 py-1">
    {caption && <span className="text-[9.5px] font-semibold uppercase tracking-[0.1em] text-muted">{caption}</span>}
    <span
      className={`whitespace-nowrap font-sheet tabular-nums ${
        strong ? "text-[15px] font-bold text-brand-deep-ink" : "text-[13.5px] font-semibold text-ink"
      }`}
    >
      {value}
    </span>
  </div>
);

// Празни колони до края на реда
export const TotalsBlankCell = ({ span, last }: { span: number; last?: boolean }) => (
  <div className={`${SPAN[span]} ${last ? "" : "border-r border-shade-2"}`} />
);
